<template>
  <f7-page name="facturacion-ejercicios" @page:init="onPageInit">
    <f7-navbar>
      <f7-nav-left><f7-link back icon-f7="arrow_left"></f7-link></f7-nav-left>
      <!-- #248 · el rótulo que fijó dirección el 05/10/2026, y nombra las DOS magnitudes que la
           pantalla enseña: el conmutador de abajo tiene «Facturación» y «Cobro real», y el nombre
           anterior solo nombraba una, así que quien entraba buscando el cobro no sabía que estaba
           aquí. Es el mismo texto que la tarjeta de entrada de la pestaña. -->
      <f7-nav-title>FACTURACIÓN Y COBRO POR EJERCICIOS</f7-nav-title>
      <f7-nav-right>
        <f7-link @click="cargar" icon-f7="arrow_2_circlepath" style="color:#c8d3e5"></f7-link>
      </f7-nav-right>
    </f7-navbar>
    <!--
      #16 · se lee en directo de factcobro_mes en la propia petición, sin pasar por ninguna caché.
      Y solo cuando hay cifras en pantalla (#169): el sello dice de cuándo son LOS DATOS QUE ESTA
      PANTALLA ENSEÑA, así que con la pestaña cerrada, con un 403 de rol o sin ejercicios cargados
      no tiene de qué hablar, y un «Datos en vivo» encima de un aviso de que no hay cifras
      contradice al propio aviso.
    -->
    <div class="sello-datos-bar" v-if="hayCifras"><sello-datos estado="vivo" /></div>
    <f7-page-content style="padding-top:0" v-roles="['ADMIN','DIRECTOR']">

      <!--
        PESTAÑA CERRADA (#169) · el servidor no puede responder de las cifras porque la tabla de los
        meses no garantiza una sola ficha por mes. El texto lo escribe la API: aquí no se reescribe ni
        se resume, que es lo que convertiría un aviso accionable en un «ha habido un problema».
        Se enseña SOLO esto: con la pestaña cerrada, el conmutador y los titulares no tienen de dónde
        salir y una pantalla a medias haría dudar de si lo que se ve es bueno.
      -->
      <f7-block v-if="pestanaCerrada" class="aviso-cerrada">
        {{ pestanaCerrada }}
      </f7-block>

      <!-- CONMUTADOR · cambia la pantalla entera, no solo el gráfico -->
      <div class="conmutador-bar" v-if="!pestanaCerrada">
        <div class="conmutador">
          <button type="button" class="conmutador-btn" :class="{'conmutador-btn-activo': magnitud === 'FACTURACION'}"
            :aria-pressed="magnitud === 'FACTURACION' ? 'true' : 'false'" @click="magnitud = 'FACTURACION'">Facturación</button>
          <button type="button" class="conmutador-btn" :class="{'conmutador-btn-activo': magnitud === 'COBRO_REAL'}"
            :aria-pressed="magnitud === 'COBRO_REAL' ? 'true' : 'false'" @click="magnitud = 'COBRO_REAL'">Cobro real</button>
        </div>
        <span class="conmutador-periodo" v-if="serie">
          A igualdad de meses: <strong>{{ serie.periodoComparable || 'sin meses cerrados' }}</strong>
        </span>
      </div>

      <!--
        FACTURAS PARA REVISAR (#169) · facturas del mes en curso que dicen estar emitidas en él pero
        se registraron antes de que empezara y antes del último cierre: la firma de una factura
        re-fechada. El texto del porqué lo manda la API, como la nota de cobro real, porque es una
        decisión de dirección sobre el dato. Aquí NO se recalcula el indicio ni se descuenta nada:
        estas facturas cuentan en las cifras de la pantalla igual que las demás.
      -->
      <div class="aviso-revisar" v-if="facturasARevisar.length && !pestanaCerrada">
        <p class="aviso-revisar-titulo">
          <f7-icon f7="exclamationmark_triangle" class="aviso-revisar-icono"></f7-icon>
          {{ facturasARevisar.length }}
          {{ facturasARevisar.length === 1 ? 'factura para revisar' : 'facturas para revisar' }}
        </p>
        <ul class="aviso-revisar-lista">
          <li v-for="f in facturasARevisar" :key="f.codigo || f.numero">
            <strong>{{ f.codigo || ('nº ' + f.numero) }}</strong>
            <span v-if="f.tipo === 'ABONO'" class="aviso-revisar-abono">abono</span>
            · emitida el {{ formatFecha(f.fechaEmision) }}
            · registrada el {{ formatFecha(f.registrada) }}
            · {{ formatEuros(f.importe) }} €
          </li>
        </ul>
        <p class="aviso-revisar-nota">{{ notaFacturasARevisar }}</p>
      </div>

      <f7-block v-if="cargando" class="text-align-center"><f7-preloader></f7-preloader></f7-block>

      <f7-block v-if="error" class="aviso-error">
        {{ error }}
      </f7-block>

      <template v-if="serie && !cargando">

        <!-- TITULARES -->
        <div class="titulares">
          <div class="titular">
            <p class="titular-rotulo">{{ titulares.periodoComparable || '—' }} de {{ titulares.anio }}</p>
            <p class="titular-cifra">{{ formatEuros(titulares.acumuladoAnio) }} €</p>
            <p class="titular-nota">Acumulado del año en curso hasta el último mes cerrado</p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">{{ titulares.periodoComparable || '—' }} de {{ titulares.anioAnterior }}</p>
            <p class="titular-cifra">{{ formatEuros(titulares.acumuladoAnioAnterior) }} €</p>
            <p class="titular-nota">El mismo periodo del año anterior</p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Variación</p>
            <p class="titular-cifra" :class="claseVar(titulares.variacionPorcentaje)">
              {{ formatEurosConSigno(titulares.variacionImporte) }} €
            </p>
            <p class="titular-nota" :class="claseVar(titulares.variacionPorcentaje)">
              {{ formatPorcentaje(titulares.variacionPorcentaje) }}
            </p>
          </div>
          <div class="titular">
            <p class="titular-rotulo">Media mensual</p>
            <p class="titular-cifra">{{ formatEuros(titulares.mediaMensual) }} €</p>
            <p class="titular-nota">
              Sobre los {{ titulares.mesesComparables }} meses cerrados del periodo, no sobre doce
            </p>
          </div>
        </div>

        <!-- NOTA D-COBRO-FUENTE · solo en la vista de cobro -->
        <div class="nota-cobro" v-if="magnitud === 'COBRO_REAL' && notaCobroReal">
          <f7-icon f7="info_circle" class="nota-cobro-icono"></f7-icon>
          <span>{{ notaCobroReal }}</span>
        </div>

        <!--
          TABLA COMPLETA · la vista de datos y, desde la #184, el primer bloque tras las tarjetas.
          Sube aquí porque es lo que de verdad se consulta: la cifra exacta de un mes concreto. Los
          dos gráficos que vienen debajo son la lectura de un vistazo de estos mismos números, no
          otro dato.
        -->
        <div class="bloque">
          <p class="bloque-titulo">Todos los meses — {{ serie.etiqueta }}</p>
          <div class="tabla-scroll">
            <table class="tabla-fc">
              <thead>
                <tr>
                  <th class="col-sticky col-anio">AÑO</th>
                  <th v-for="m in nombresMes" :key="'th-' + m" class="col-mes">{{ m }}</th>
                  <th class="col-total">TOTAL AÑO</th>
                  <th class="col-total-comp">{{ serie.periodoComparable || '—' }}</th>
                  <th class="col-var">% VAR {{ serie.periodoComparable || '' }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ej in serie.ejercicios" :key="'tr-' + ej.anio" :class="{'fila-actual': ej.enCurso}">
                  <td class="col-sticky col-anio"><strong>{{ ej.anio }}</strong></td>
                  <td v-for="(val, idx) in ej.meses" :key="'td-' + ej.anio + '-' + idx" class="col-mes"
                    :class="{'celda-vacia': val == null}">{{ formatEuros(val) }}</td>
                  <td class="col-total"><strong>{{ formatEuros(ej.total) }}</strong></td>
                  <td class="col-total-comp"><strong>{{ formatEuros(ej.acumuladoComparable) }}</strong></td>
                  <td class="col-var" :class="claseVar(ej.variacionComparablePorcentaje)">
                    {{ formatPorcentaje(ej.variacionComparablePorcentaje) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="bloque-pie">
            Ejercicios cargados: {{ rotuloEjerciciosCargados }}. Un mes sin cerrar sale vacío, no como
            cero. Los importes se enseñan en euros enteros; las sumas se hacen con los céntimos
            guardados, así que un total puede no ser la suma exacta de los redondeos que se ven.
          </p>
        </div>

        <!-- BARRAS · acumulado al mismo mes, año por año -->
        <div class="bloque">
          <p class="bloque-titulo">
            Acumulado {{ serie.periodoComparable || '—' }}, año por año — {{ serie.etiqueta }}
          </p>
          <div class="barras">
            <div class="barra-fila" v-for="b in barras" :key="'b-' + b.anio" :class="{'barra-fila-actual': b.enCurso}">
              <span class="barra-anio">{{ b.anio }}</span>
              <span class="barra-pista">
                <span class="barra-valor" :style="{ width: b.porcentajeAncho + '%', background: b.color }"></span>
              </span>
              <span class="barra-importe">{{ formatEuros(b.importe) }} €</span>
              <span class="barra-var" :class="claseVar(b.porcentaje)">{{ formatPorcentaje(b.porcentaje) }}</span>
            </div>
          </div>
          <p class="bloque-pie">
            La misma ventana de meses en todos los años. La variación de cada barra es contra el año
            inmediatamente anterior, no contra el año en curso.
          </p>
        </div>

        <!--
          ACUMULADO MES A MES (#184) · dos barras por mes sobre la MISMA línea de salida: detrás la
          gruesa del año anterior, delante la fina del año en curso. Sustituye al gráfico de líneas
          de los siete ejercicios de la #171, que es exactamente lo que dirección no conseguía leer:
          con siete trazos se ve la forma de cada año, pero no si vamos por delante o por detrás del
          anterior en un mes concreto. Dos barras encaradas sí.

          La escala de las doce filas es UNA SOLA —el año completo del ejercicio anterior— y no el
          máximo de cada fila: con una escala por fila todas saldrían igual de largas y la
          comparación entre meses, que es para lo que está el cuadro, se perdería.
        -->
        <div class="bloque" v-if="mesAMes.filas.length">
          <p class="bloque-titulo">Acumulado mes a mes — {{ serie.etiqueta }}</p>
          <div class="mes-a-mes">
            <!-- La leyenda va ARRIBA, antes de las barras: las dos series se distinguen por grosor
                 además de por color, y eso hay que haberlo leído antes de mirar la primera fila. -->
            <div class="mm-leyenda">
              <span class="mm-leyenda-serie">
                <span class="mm-muestra mm-muestra-anterior"></span>
                Acumulado de {{ mesAMes.anioAnterior }} hasta ese mes
              </span>
              <span class="mm-leyenda-serie">
                <span class="mm-muestra mm-muestra-actual"></span>
                Acumulado de {{ mesAMes.anioActual }} hasta ese mes
              </span>
              <span class="mm-leyenda-nota">
                A la derecha de cada fila: el acumulado de {{ mesAMes.anioActual }} y su diferencia
                contra el mismo mes de {{ mesAMes.anioAnterior }}.
              </span>
            </div>
            <div class="mm-fila" v-for="f in mesAMes.filas" :key="'mm-' + f.mes"
              :class="{'mm-fila-abierta': !f.cerrado}">
              <span class="mm-mes">{{ f.nombre }}</span>
              <span class="mm-pista">
                <!-- Cada barra se dibuja solo si su año tiene dato en ese mes. Un mes sin cerrar NO
                     lleva barra de cero del año en curso: una barra de ancho cero al lado de la del
                     año anterior se lee como «este mes no se ha facturado nada», y lo que pasa es
                     que todavía no se sabe. -->
                <span class="mm-barra mm-barra-anterior" v-if="f.anchoAnterior != null"
                  :class="{'mm-barra-rebosa': f.rebosaAnterior}"
                  :style="{ width: f.anchoAnterior + '%' }"></span>
                <!-- Una barra que se sale de la escala llega al final de la pista y lo dice con el
                     extremo rayado: sin esa marca, su longitud se leería como «vale la escala». El
                     valor de verdad está siempre en la columna de la derecha. -->
                <span class="mm-barra mm-barra-actual" v-if="f.anchoActual != null"
                  :class="{'mm-barra-rebosa': f.rebosaActual}"
                  :style="{ width: f.anchoActual + '%' }"></span>
              </span>
              <span class="mm-importe">
                <template v-if="f.importeActual == null">—</template>
                <template v-else>{{ formatEuros(f.importeActual) }} €</template>
              </span>
              <span class="mm-dif" :class="claseVar(f.diferencia)">
                <template v-if="f.diferencia == null">—</template>
                <template v-else>{{ formatEurosConSigno(f.diferencia) }} €</template>
              </span>
            </div>
          </div>
          <p class="bloque-pie">{{ pieMesAMes }}</p>
        </div>

      </template>

      <f7-block v-if="!cargando && !error && !pestanaCerrada && !serie" class="text-align-center">
        <p>No hay ejercicios cargados todavía.</p>
      </f7-block>

    </f7-page-content>
  </f7-page>
</template>
<style scoped>
.sello-datos-bar { padding: 6px 16px 0; }

/* CONMUTADOR */
.conmutador-bar {
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
  padding: 10px 16px 0;
}
.conmutador {
  display: inline-flex; border: 1px solid #c8d3e5; border-radius: 8px; overflow: hidden;
}
.conmutador-btn {
  border: 0; background: white; color: #1a2744;
  font-size: 13px; font-weight: 600; padding: 7px 16px; cursor: pointer;
}
.conmutador-btn + .conmutador-btn { border-left: 1px solid #c8d3e5; }
.conmutador-btn-activo { background: #1a2744; color: white; }
.conmutador-periodo { font-size: 12px; color: #7c8db0; }

.aviso-error {
  background: #FAECE7; border-left: 4px solid #D85A30; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #7a2d12; margin: 12px 16px;
}

/*
 * El aviso de pestaña cerrada (#169). Se distingue del de error de carga a propósito: no es «no se
 * ha podido cargar, reintenta» —reintentar no lo arregla— sino una explicación que hay que leer
 * entera, así que ocupa más y va con el interlineado de un párrafo.
 */
.aviso-cerrada {
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 14px 16px; font-size: 14px; color: #5c3d05; margin: 16px;
  line-height: 1.5; max-width: 760px;
}

/*
 * Facturas para revisar (#169). Es un aviso que pide una acción —mirar esas facturas una por una—,
 * así que se distingue de la nota de cobro real, que es solo una aclaración: lleva icono de atención
 * y va arriba, antes de las cifras, porque es información sobre ellas.
 */
.aviso-revisar {
  background: #FFF6E5; border-left: 4px solid #C8862A; border-radius: 6px;
  padding: 12px 14px; margin: 12px 16px; max-width: 760px; color: #5c3d05;
}
.aviso-revisar-titulo {
  display: flex; align-items: center; gap: 6px;
  margin: 0; font-size: 14px; font-weight: 700;
}
.aviso-revisar-icono { color: #C8862A; font-size: 18px; }
.aviso-revisar-lista { margin: 8px 0 0; padding-left: 18px; font-size: 13px; line-height: 1.5; }
.aviso-revisar-abono {
  background: #C8862A; color: white; border-radius: 4px;
  font-size: 10px; font-weight: 700; padding: 1px 5px; margin-left: 4px; text-transform: uppercase;
}
.aviso-revisar-nota { margin: 8px 0 0; font-size: 12px; line-height: 1.4; }

/* TITULARES */
.titulares {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px; padding: 12px 16px;
}
.titular {
  background: white; border: 1px solid #e0e0e0; border-radius: 10px;
  border-left: 4px solid #185FA5; padding: 12px 14px;
}
.titular-rotulo { font-size: 11px; color: #7c8db0; margin: 0; text-transform: uppercase; letter-spacing: .4px; }
.titular-cifra { font-size: 21px; font-weight: 700; color: #1a2744; margin: 4px 0 0; }
.titular-nota { font-size: 11px; color: #7c8db0; margin: 4px 0 0; line-height: 1.3; }
.var-positivo { color: #0F6E56; }
.var-negativo { color: #C0392B; }

/* NOTA D-COBRO-FUENTE */
.nota-cobro {
  display: flex; gap: 8px; align-items: flex-start;
  background: #FAEEDA; border-left: 4px solid #BA7517; border-radius: 6px;
  padding: 10px 12px; margin: 0 16px 12px; font-size: 12px; color: #6b4a0c; line-height: 1.4;
}
.nota-cobro-icono { color: #BA7517; font-size: 18px; flex: 0 0 auto; }

/* BLOQUES */
.bloque { margin: 0 16px 18px; }
.bloque-titulo {
  font-size: 12px; font-weight: 700; color: #1a2744; margin: 0 0 6px;
  text-transform: uppercase; letter-spacing: .4px;
}
.bloque-pie { font-size: 11px; color: #7c8db0; margin: 6px 0 0; line-height: 1.4; }

/* BARRAS · acumulado al mismo mes, año por año */
.barras { background: white; border: 1px solid #e0e0e0; border-radius: 10px; padding: 10px 12px; }
.barra-fila { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 12px; }
.barra-fila-actual { font-weight: 700; }
.barra-anio { width: 40px; flex: 0 0 auto; color: #1a2744; }
.barra-pista { flex: 1 1 auto; background: #f2f5fa; border-radius: 3px; height: 14px; display: block; }
.barra-valor { display: block; height: 14px; border-radius: 3px; }
.barra-importe { width: 92px; flex: 0 0 auto; text-align: right; color: #1a2744; }
.barra-var { width: 64px; flex: 0 0 auto; text-align: right; }

/*
 * ACUMULADO MES A MES (#184) · las dos barras arrancan de la MISMA línea de salida, así que van
 * posicionadas en absoluto sobre la pista y no en flujo: encadenadas una tras otra se leerían como
 * una suma de las dos series, que es justo lo contrario de lo que el cuadro compara.
 *
 * La del año en curso es la fina y va DELANTE, centrada sobre la gruesa del año anterior. Así
 * cuando va por detrás se ve el reborde del año anterior asomando a su derecha, y cuando va por
 * delante sobresale ella: el signo de la diferencia se lee en la silueta, sin mirar la cifra.
 */
.mes-a-mes { background: white; border: 1px solid #e0e0e0; border-radius: 10px; padding: 10px 12px; }
.mm-leyenda {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px;
  font-size: 11px; color: #7c8db0; padding-bottom: 8px; margin-bottom: 4px;
  border-bottom: 1px solid #edf1f7;
}
.mm-leyenda-serie { display: inline-flex; align-items: center; gap: 6px; }
.mm-muestra { display: inline-block; width: 18px; border-radius: 2px; flex: 0 0 auto; }
.mm-muestra-anterior { height: 14px; background: #185FA5; }
.mm-muestra-actual { height: 7px; background: #0F6E56; }
.mm-leyenda-nota { flex: 1 1 100%; line-height: 1.4; }
.mm-fila { display: flex; align-items: center; gap: 8px; padding: 3px 0; font-size: 12px; }
.mm-mes {
  width: 34px; flex: 0 0 auto; color: #1a2744; font-weight: 600;
  font-size: 11px; letter-spacing: .3px;
}
/* Un mes sin cerrar se atenúa: su fila está a medias a propósito, y sin esto parece un mes cerrado
   al que le falta la barra del año en curso. */
.mm-fila-abierta .mm-mes { color: #9aa7bd; font-weight: 500; }
.mm-pista {
  position: relative; flex: 1 1 auto; min-width: 80px;
  background: #f2f5fa; border-radius: 3px; height: 14px; display: block;
}
.mm-barra { position: absolute; left: 0; display: block; border-radius: 3px; }
.mm-barra-anterior { top: 0; height: 14px; background: #185FA5; opacity: .45; }
/* Delante de la gruesa y centrada sobre ella: (14 - 7) / 2 = 3,5 px de margen arriba. */
.mm-barra-actual { top: 3.5px; height: 7px; background: #0F6E56; }
/*
 * La escala es el año entero del ejercicio anterior y no se mueve, así que el año en curso puede
 * superarla. Esa barra se recorta al final de la pista, y el recorte NO puede quedar mudo: una
 * barra recortada mide la escala en vez de su valor, y dos filas distintas se verían iguales.
 *
 * Se marca con el extremo cuadrado y rayado, DENTRO de la propia barra: cualquier marca que
 * ocupara sitio en la fila —un icono, una columna más— se comería el presupuesto de anchura del
 * móvil que se cuadra abajo. Los degradados se escriben con la sintaxis de parada única, que es la
 * que entiende el Chrome 49 del `browserslist` de este repo.
 */
.mm-barra-rebosa { border-top-right-radius: 0; border-bottom-right-radius: 0; }
.mm-barra-rebosa::after {
  content: ''; position: absolute; right: 0; top: 0; bottom: 0; width: 9px;
  background: repeating-linear-gradient(135deg,
    rgba(255, 255, 255, .85) 0, rgba(255, 255, 255, .85) 2px,
    rgba(255, 255, 255, 0) 2px, rgba(255, 255, 255, 0) 4px);
}
.mm-importe {
  width: 96px; flex: 0 0 auto; text-align: right; color: #1a2744;
  font-variant-numeric: tabular-nums;
}
.mm-dif {
  width: 86px; flex: 0 0 auto; text-align: right; font-weight: 600;
  font-variant-numeric: tabular-nums;
}
/*
 * En un móvil estrecho la fila no cabe, y lo que sobra lo arrastra la PÁGINA entera, que es justo
 * el scroll horizontal que la #184 prohíbe. La cuenta: 34 px del mes + 80 de pista mínima + 96 del
 * acumulado + 86 de la diferencia + 3 huecos de 8 = 320 px, y a 375 px de pantalla, descontando los
 * 16 de margen de `.bloque` por lado, el borde de la tarjeta y sus 12 de relleno, quedan 317. A
 * 360 px quedan 302. Empieza a desbordar por debajo de 378 px, así que el corte va a 420.
 *
 * Se estrechan las columnas en vez de meter la tarjeta en un contenedor con scroll como la tabla:
 * unas barras que se desplazan a lo ancho pierden por la izquierda la línea de salida, y la línea
 * de salida común es lo único que hace legible la comparación de las dos series. Las cifras siguen
 * cabiendo —a 12 px con `tabular-nums`, «3.640.550 €» mide unos 64 px y «−123.629 €» otros tantos—,
 * así que estrechar la caja no parte el número ni obliga a bajar la letra.
 *
 * Con esto la fila ocupa 30 + 48 + 80 + 72 + 3×8 = 254 px, que entra incluso en los 262 que deja
 * una pantalla de 320 px. El hueco de 8 px se queda como está a propósito: el `gap` de flexbox pide
 * Chrome 84 y el `browserslist` de este repo baja a Chrome 49, así que en los navegadores del suelo
 * no se aplica —la fila sale 24 px MÁS estrecha, no más ancha— y escribir un `gap` nuevo para
 * ahorrar 6 px sería apostar la cuenta a una propiedad que puede no estar.
 */
@media (max-width: 420px) {
  .mm-mes { width: 30px; }
  .mm-pista { min-width: 48px; }
  .mm-importe { width: 80px; }
  .mm-dif { width: 72px; }
}

/*
 * TABLA · los importes a 15 px y las cabeceras a 12 px (#184). Venían de 11 y 10 px, que es tamaño
 * de nota al pie, y esta tabla pasó a ser el primer bloque de la pantalla y el que se consulta: se
 * lee sentado a la mesa, no acercándose al monitor.
 *
 * `tabular-nums` no es un adorno: con la cifra de ancho variable las columnas de una tabla de doce
 * meses no cuadran en vertical y hay que ir leyendo número a número en vez de recorrer la columna.
 */
.tabla-scroll { overflow-x: auto; background: white; border: 1px solid #e0e0e0; border-radius: 10px; }
.tabla-fc { border-collapse: collapse; width: 100%; font-size: 15px; }
.tabla-fc th, .tabla-fc td {
  padding: 6px 9px; text-align: right; white-space: nowrap; border-bottom: 1px solid #edf1f7;
  font-variant-numeric: tabular-nums;
}
.tabla-fc thead th { background: #f7f9fc; color: #7c8db0; font-size: 12px; text-transform: uppercase; letter-spacing: .3px; }
.col-sticky { position: sticky; left: 0; background: white; text-align: left; z-index: 1; }
.tabla-fc thead .col-sticky { background: #f7f9fc; }
.col-anio { min-width: 58px; }
/* La línea fina que separa los meses de los totales: sin ella, TOTAL AÑO se lee como un mes más. */
.col-total { border-left: 1px solid #d7e0ee; }
.col-total, .col-total-comp { background: #fbfcfe; }
.celda-vacia { color: #c8d3e5; }
.fila-actual { background: #E6F1FB; }
.fila-actual .col-sticky { background: #E6F1FB; }
/* El año en curso, en el mismo verde con el que se le distingue en los dos gráficos de abajo. */
.fila-actual .col-anio { color: #0F6E56; }
</style>
<script>
import { unwrapDto } from '../../js/db/responseUtils.js';
import FactCobro from '../../js/db/FactCobro.js';
import SelloDatos from '../../components/SelloDatos.vue';
import cerrojoDeLaSesion from '../../js/utils/cerrojoDeLaSesion.js';

const NOMBRES_MES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

export default {
  components: { SelloDatos },
  data: function() {
    return {
      cargando: false,
      error: null,
      // El aviso de pestaña cerrada (#169), tal como lo manda el servidor. Separado de `error`
      // porque no es lo mismo: `error` invita a reintentar y esto no se arregla reintentando.
      pestanaCerrada: null,
      magnitud: 'FACTURACION',
      anioActual: null,
      aniosDisponibles: [],
      notaCobroReal: null,
      // Las facturas del mes en curso que conviene revisar y el texto que explica por qué (#169).
      // Las dos cosas vienen del servidor: el aviso es sobre el dato, no sobre la pantalla.
      facturasARevisar: [],
      notaFacturasARevisar: null,
      series: {},
      nombresMes: NOMBRES_MES,
      // Nº de petición: descarta las respuestas que llegan tarde, para que un refresco pulsado dos
      // veces no deje en pantalla la primera respuesta por encima de la segunda.
      peticionEnCurso: 0
    };
  },
  computed: {
    serie: function() {
      return this.series[this.magnitud] || null;
    },
    /**
     * Si la pantalla está enseñando cifras ahora mismo. Es la misma condición con la que se pinta
     * el bloque de los titulares y el gráfico, y la que gobierna el sello de datos: enseñarlo
     * cuando no hay nada que sellar es afirmar algo sobre unos datos que no están.
     *
     * Es una sola condición y no una lista de estados a los que esconderlo (`!pestanaCerrada`,
     * `!error`, …) a propósito: así el estado nuevo que se añada mañana no se olvida de apagarlo.
     */
    hayCifras: function() {
      return !!this.serie && !this.cargando;
    },
    titulares: function() {
      return (this.serie && this.serie.titulares) || {};
    },
    /**
     * Qué ejercicios hay cargados. Se rotula al pie de la tabla porque es lo que deja ver de un
     * vistazo si una carga dejó un año fuera: la tabla sin esa línea solo enseña los años que hay,
     * y nadie echa en falta una fila que no sabe que debería estar.
     */
    rotuloEjerciciosCargados: function() {
      const anios = this.aniosDisponibles || [];
      if (!anios.length) return 'ninguno';
      if (anios.length === 1) return String(anios[0]);
      // Si no hay huecos, el rango basta; si falta un año en medio, se enumeran todos para que el
      // hueco se vea.
      const contiguos = anios[anios.length - 1] - anios[0] + 1 === anios.length;
      return contiguos ? anios[0] + '-' + anios[anios.length - 1] : anios.join(', ');
    },
    /** Los ejercicios de más antiguo a más reciente. */
    ejerciciosAscendentes: function() {
      if (!this.serie || !this.serie.ejercicios) return [];
      return this.serie.ejercicios.slice().sort(function(a, b) { return a.anio - b.anio; });
    },
    /** Las barras del acumulado comparable, de más reciente a más antiguo. */
    barras: function() {
      const self = this;
      if (!this.serie || !this.serie.ejercicios) return [];
      const conDato = this.serie.ejercicios.filter(function(ej) { return ej.acumuladoComparable != null; });
      let maximo = 0;
      conDato.forEach(function(ej) {
        if (Number(ej.acumuladoComparable) > maximo) maximo = Number(ej.acumuladoComparable);
      });
      return conDato.map(function(ej) {
        return {
          anio: ej.anio,
          enCurso: !!ej.enCurso,
          importe: ej.acumuladoComparable,
          porcentaje: ej.variacionComparablePorcentaje,
          porcentajeAncho: maximo > 0 ? (100 * Number(ej.acumuladoComparable)) / maximo : 0,
          color: ej.enCurso ? '#0F6E56' : (ej.anio === self.anioActual - 1 ? '#185FA5' : '#9aa7bd')
        };
      });
    },
    /**
     * Las doce filas del acumulado mes a mes (#184): en cada una, el acumulado del año anterior y
     * el del año en curso hasta ese mismo mes, y la diferencia entre los dos.
     *
     * La diferencia se resta **aquí** y no viene del servidor: son dos cifras que el DTO ya trae y
     * en esta tarea el endpoint no se toca. Lo que eso obliga es a que la resta sea la misma que la
     * de la tarjeta de Variación —los mismos dos acumulados del último mes cerrado—, porque las dos
     * se ven en la misma pantalla y si no cuadran una de las dos está mintiendo.
     */
    mesAMes: function() {
      const self = this;
      const ejercicios = this.ejerciciosAscendentes;
      function ejercicioDe(anio) {
        return ejercicios.filter(function(e) { return e.anio === anio; })[0] || null;
      }
      /** El acumulado de un ejercicio en un mes, o `null` si ese mes no tiene dato. */
      function acumulado(ej, mes) {
        const v = ej && ej.acumulados ? ej.acumulados[mes] : null;
        return v == null ? null : Number(v);
      }

      const anioActual = this.anioActual;
      const anioAnterior = anioActual == null ? null : anioActual - 1;
      const enCurso = ejercicioDe(anioActual);
      const anterior = ejercicioDe(anioAnterior);
      const vacio = {
        filas: [], escala: 0, escalaEsAnioAnterior: false, mesQueRebosa: null,
        anioActual: anioActual, anioAnterior: anioAnterior
      };
      // Sin el ejercicio anterior no hay con qué comparar ni de dónde sacar la escala, y este cuadro
      // es exactamente esa comparación: se esconde entero antes que enseñar doce filas de una sola
      // barra. La tabla y las barras ENE-SEP siguen enseñando lo que haya cargado.
      if (!anterior) return vacio;

      // La escala de las doce filas es el AÑO COMPLETO del ejercicio anterior, y no el máximo de
      // cada fila: con una escala por fila las doce saldrían igual de largas y no se podrían
      // comparar entre sí, que es para lo que está el cuadro.
      //
      // La escala y lo que el pie dice de ella se deciden AQUÍ, en la misma cuenta, y el pie no
      // hace más que redactar `escalaEsAnioAnterior`. Es lo que impide que vuelva a pasar lo de la
      // ronda 1: la frase se escribía en un sitio y el número en otro, y bastaba un caso de los
      // bordes para que dejaran de hablar de lo mismo. La regla es una sola: **la pista completa
      // vale `escala`, y si el pie promete el año entero del anterior es porque `escala` ES ese
      // año entero**.
      //
      // Lo que esa regla NO dice es que la escala pueda moverse para que quepa todo: la escala es
      // el año entero del anterior siempre que se conozca, y una barra que lo supere se recorta y
      // se marca. Ver el comentario del respaldo, más abajo.
      //
      // El año entero del ejercicio anterior es su acumulado de DICIEMBRE, no su `total`.
      // `FactCobroEjercicioDTO` documenta `total` como «la suma de sus meses con dato», así que a
      // un ejercicio con un hueco —o al más antiguo cargado, que puede empezar a mitad de año— le
      // sale un total positivo que no es un año entero. El acumulado de diciembre sí lo es, pero
      // solo si están los doce, y eso se comprueba aquí en vez de confiar en que el servidor corte
      // la serie en el primer hueco: entre esta pantalla y la API no hay contrato publicado que
      // obligue a ese corte, y el día que cambie el pie volvería a prometer un año que no está.
      // Se mira el acumulado y no `mesesConDato`, que diría que el año está completo pero no cuál
      // es su cifra: con dos datos distintos, el número y la promesa pueden volver a separarse.
      const docePresentes = NOMBRES_MES.every(function(nombre, mes) {
        return acumulado(anterior, mes) != null;
      });
      const anioCompletoAnterior = docePresentes ? acumulado(anterior, 11) : null;
      // El respaldo es SOLO para cuando ese año entero no se conoce. Mientras exista, es la escala
      // pase lo que pase con el año en curso: la referencia de este cuadro la fija la #184 —«el
      // ancho total es el año completo del ejercicio anterior»— y si creciera cuando el año en
      // curso lo supera, DIC del anterior dejaría de llenar la pista justo en los ejercicios con
      // crecimiento, que son en los que el cuadro se mira. Lo que se sale se recorta al 100 % y se
      // DICE (`rebosa`), que es lo que impide que una barra recortada se lea como si valiera la
      // escala.
      let mayorDibujado = 0;
      NOMBRES_MES.forEach(function(nombre, mes) {
        mayorDibujado = Math.max(mayorDibujado, acumulado(anterior, mes) || 0, acumulado(enCurso, mes) || 0);
      });
      const esAnioAnterior = anioCompletoAnterior != null && anioCompletoAnterior > 0;
      const escala = esAnioAnterior ? anioCompletoAnterior : mayorDibujado;
      if (!(escala > 0)) return vacio;

      const filas = NOMBRES_MES.map(function(nombre, mes) {
        const vAnterior = acumulado(anterior, mes);
        const vActual = acumulado(enCurso, mes);
        return {
          mes: mes,
          nombre: nombre,
          // Un mes está cerrado cuando el AÑO EN CURSO tiene acumulado en él. Es lo que decide las
          // tres cosas que distinguen a OCT, NOV y DIC: ni barra fina, ni importe, ni diferencia.
          cerrado: vActual != null,
          importeActual: vActual,
          importeAnterior: vAnterior,
          // `null` significa que esa barra NO se dibuja. Un cero dibujaría una barra de ancho cero,
          // que al lado de la del año anterior se lee como «este mes no se facturó nada» cuando lo
          // que pasa es que todavía no se sabe.
          anchoAnterior: vAnterior == null ? null : self.anchoPista(vAnterior, escala),
          anchoActual: vActual == null ? null : self.anchoPista(vActual, escala),
          // Una barra recortada se marca en su extremo. Sin la marca su longitud, leída con la
          // regla que anuncia el pie, diría la escala en vez de su valor, y dos filas recortadas
          // distintas se verían iguales.
          rebosaAnterior: vAnterior != null && vAnterior > escala,
          rebosaActual: vActual != null && vActual > escala,
          diferencia: vActual == null || vAnterior == null ? null : vActual - vAnterior
        };
      });

      // El primer mes que se sale, para que el pie lo nombre en vez de dejar al que mira buscando
      // cuál de las doce filas lleva la marca.
      const primeroQueRebosa = filas.filter(function(f) {
        return f.rebosaActual || f.rebosaAnterior;
      })[0] || null;

      return {
        filas: filas, escala: escala, escalaEsAnioAnterior: esAnioAnterior,
        mesQueRebosa: primeroQueRebosa ? primeroQueRebosa.nombre : null,
        anioActual: anioActual, anioAnterior: anioAnterior
      };
    },
    /**
     * El pie del cuadro mes a mes. Describe la escala que se **está** dibujando, no la que se
     * quería dibujar: cuando el ejercicio anterior no llega con los doce meses la escala cae en el
     * mayor acumulado pintado, y un pie que siguiera prometiendo «el año entero de 2025» haría leer
     * las doce filas con una regla que no es la suya. Y si alguna barra se sale de la pista, lo
     * dice: la regla sigue siendo la misma, pero hay longitudes que no se pueden leer con ella.
     *
     * **Aquí no se decide nada**: la rama la elige `escalaEsAnioAnterior`, el número sale de
     * `escala` y el aviso del exceso de `mesQueRebosa`, los tres de la misma cuenta de `mesAMes`.
     * Este getter solo redacta. Recalcular aquí la condición —mirar otra vez `total`, contar meses,
     * o comparar importes con la escala— es lo que haría que la frase y el dibujo pudieran volver a
     * discrepar.
     *
     * Se escribe aquí y no repartido en dos `<template>` de la plantilla por dos razones: así la
     * frase se puede comprobar sin navegador, y así no depende de que el compilador de plantillas
     * conserve el espacio entre los trozos —con `whitespace: condense` dos frases en ramas
     * distintas se pegan sin separación.
     */
    pieMesAMes: function() {
      const mm = this.mesAMes;
      const escala = this.formatEuros(mm.escala) + ' €';
      // La rama de respaldo no dice POR QUÉ no es el año entero: el motivo es que al ejercicio
      // anterior le faltan meses, pero puede faltarle el último o uno de en medio, y nombrar el
      // caso equivocado es peor que no nombrarlo. Lo que necesita quien mira las barras es qué mide
      // la pista, y eso es la cifra.
      const base = mm.escalaEsAnioAnterior
        ? 'Las doce filas comparten escala: el ancho completo de la pista es el año entero de '
          + mm.anioAnterior + ' (' + escala + '), así que las filas se comparan entre sí.'
        : 'Las doce filas comparten escala y se comparan entre sí, pero no es el año entero de '
          + mm.anioAnterior + ': la pista completa es el mayor acumulado de los que se dibujan ('
          + escala + ').';
      // El exceso se nombra en vez de ensanchar la pista para que quepa. Ensancharla dejaría al
      // año entero del anterior sin llenar la pista —que es la referencia del cuadro— y justo en
      // los ejercicios que van creciendo, que son en los que se mira.
      const exceso = mm.mesQueRebosa
        ? ' Desde ' + mm.mesQueRebosa + ' el acumulado supera esa escala: esas barras llegan al'
          + ' final de la pista con el extremo rayado y su cifra exacta está a la derecha.'
        : '';
      // De un mes sin cerrar el cuadro garantiza que NO lleva la barra fina ni las dos cifras. Que
      // lleve la gruesa es otra cosa y depende del año anterior: a un ejercicio histórico con un
      // hueco en ese mes le falta también esa, y el pie no puede prometerla.
      return base + exceso + ' Un mes sin cerrar no dibuja la barra de ' + mm.anioActual
        + ' y lleva un guion en las dos columnas de la derecha.';
    }
  },
  watch: {
    /**
     * El conmutador Facturación / Cobro real **no pide nada al servidor**: las dos magnitudes
     * vienen en la misma respuesta y esto solo cambia una variable local. Lo que sí hace es volver
     * a pintar la pantalla entera —la tabla y los dos gráficos—, así que medirlo da el coste de
     * PINTAR con la red fuera de la ecuación, que es precisamente lo que la #209 pide para poder
     * descartar el navegador.
     *
     * Y de paso deja acreditado en el propio registro que al conmutar no hay red: su línea sale con
     * `red=—` y sin ninguna línea de servidor detrás.
     */
    magnitud: function() {
      this.medirPintado('conmutar', this.ahoraMedido(), null);
    }
  },
  methods: {
    onPageInit: function() {
      this.cargar();
    },
    cargar: function() {
      const self = this;
      const peticion = ++self.peticionEnCurso;
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `then`
      // que lo soltaba y la instancia (`keepAlive`) lo conservaría puesto en la cuenta siguiente.
      cerrojoDeLaSesion(self, 'cargando');
      self.error = null;
      self.pestanaCerrada = null;
      const t0 = self.ahoraMedido();
      FactCobro.comparativa().then(function(res) {
        if (peticion !== self.peticionEnCurso) return;
        const tRespuesta = self.ahoraMedido();
        const dto = unwrapDto(res);
        self.cargando = false;
        if (!dto) { self.series = {}; self.facturasARevisar = []; return; }
        self.anioActual = dto.anioActual;
        self.aniosDisponibles = dto.aniosDisponibles || [];
        self.notaCobroReal = dto.notaCobroReal;
        self.facturasARevisar = dto.facturasARevisar || [];
        self.notaFacturasARevisar = dto.notaFacturasARevisar;
        self.series = { FACTURACION: dto.facturacion, COBRO_REAL: dto.cobroReal };
        // La asignación de arriba es la que dispara el pintado de la tabla y los dos gráficos: la
        // medida se cierra DESPUÉS de ella y por eso va aquí y no en un `finally`.
        self.medirPintado('abrir', t0, tRespuesta);
      }).catch(function(err) {
        if (peticion !== self.peticionEnCurso) return;
        self.cargando = false;
        self.series = {};
        // Sin cifras no hay aviso que dar: dejarlo encendido de la carga anterior afirmaría algo
        // sobre un mes del que esta pantalla ya no está enseñando nada.
        self.facturasARevisar = [];
        // La pestaña cerrada (#169) trae su propia explicación: qué pasa, por qué está cerrada y a
        // quién avisar. Se enseña TAL CUAL, porque el genérico de abajo —«vuelve a intentarlo con el
        // botón de recargar»— manda hacer justo lo que no lo arregla, y es el error genérico que
        // dirección descartó al elegir esta solución.
        if (err && err.code === FactCobro.CODIGO_PESTANA_CERRADA && err.message) {
          self.pestanaCerrada = err.message;
          return;
        }
        // Un 403 aquí no es un error de la pantalla: es la restricción de rol (D-ROL), y decirlo
        // así evita que alguien lo reporte como una avería.
        self.error = err && err.status === 403
          ? 'Esta pantalla es solo de ADMIN y DIRECTOR.'
          : 'No se han podido cargar los ejercicios. Vuelve a intentarlo con el botón de recargar.';
      });
    },
    /**
     * El reloj de la medida (#209). `performance.now()` y no `Date.now()`: es monótono y en
     * milisegundos con decimales, así que un ajuste de la hora del equipo a mitad de la carga no
     * puede dejar un tramo en negativo, y un pintado de 4 ms no se queda en «0». Con respaldo a
     * `Date.now()` por si el entorno no lo trae —Cordova en un Android viejo—: la medida pierde
     * resolución pero no desaparece.
     */
    ahoraMedido: function() {
      return typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now();
    },
    /**
     * El reparto entre **espera de red** y **pintado** de una carga o de un conmutado (#209,
     * criterio 3). No cambia nada de la pantalla: solo mira el reloj en tres puntos y escribe una
     * línea en la consola del navegador.
     *
     * Son tres tramos y cada uno contesta una cosa distinta:
     *
     *  - `red`: desde que se pide hasta que llega la respuesta. Es lo que mide el servidor, y
     *    cruzarlo con el `total` de la línea `abrir-ejercicios` de la API dice cuánto se va en la
     *    red y en la cola del navegador. `—` cuando no hubo petición (el conmutador).
     *  - `vue`: lo que tarda Vue en recalcular los `computed` y rehacer el DOM tras asignar los
     *    datos. Se cierra en `$nextTick`, que es justo cuando el ciclo de actualización acaba.
     *  - `espera-cuadro`: lo que el DOM ya rehecho pasa esperando a que el navegador abra el
     *    siguiente cuadro. No es coste de esta pantalla —a 60 Hz son hasta 16 ms y los mismos con
     *    una tabla vacía—, pero es tiempo que la persona espera, así que sale con su nombre en vez
     *    de sumarse a `frame` haciéndolo parecer caro.
     *  - `frame`: lo que el navegador tarda en maquetar y pintar ese DOM, que es la cifra con la
     *    que se descarta —o no— al navegador.
     *
     * Con la pestaña en segundo plano el navegador no entrega cuadros y la línea no sale: la medida
     * se toma con la pantalla delante, que es como se abre de verdad.
     *
     * @param motivo `abrir` o `conmutar`, para distinguir las dos formas de llegar al pintado.
     * @param t0 el instante del que cuelga el tramo: el de la petición, o el del conmutado.
     * @param tRespuesta cuándo llegó la respuesta, o `null` si no hubo petición.
     */
    medirPintado: function(motivo, t0, tRespuesta) {
      const self = this;
      // **Lo primero, que la medida no pueda tumbar la pantalla.** Esto se llama dentro del `then`
      // de la carga, así que una excepción aquí la recogería el `catch` de abajo y una carga buena
      // acabaría pintando «No se han podido cargar los ejercicios» con las cifras ya en la mano.
      // Lo que se pierde si el ciclo de actualización no está disponible es la línea del reparto,
      // que es justo lo que se puede perder.
      if (typeof self.$nextTick !== 'function') return;
      const red = tRespuesta == null ? null : tRespuesta - t0;
      // El pintado se cuenta desde la respuesta, no desde la petición: lo que se busca es separar
      // los dos costes, y arrastrar la espera de red dentro del pintado los volvería a juntar.
      const desdeDondeSePinta = tRespuesta == null ? t0 : tRespuesta;
      self.$nextTick(function() {
        const tDom = self.ahoraMedido();
        self.medirCuadro(motivo, red, tDom - desdeDondeSePinta, tDom);
      });
    },
    /**
     * El tramo de maquetar y pintar, que es el cuadro siguiente al `$nextTick`.
     *
     * **Se cierra después del pintado, no al empezar el cuadro.** El callback de
     * `requestAnimationFrame` corre ANTES de que el navegador maquete y pinte —en todos ellos—, así
     * que terminar ahí medía sobre todo la espera hasta el siguiente refresco y dejaba el coste de
     * las 7 × 12 celdas y los dos gráficos fuera de la cifra que precisamente sirve para descartar
     * al navegador. Por eso son dos puntos: el callback del cuadro marca dónde acaba la espera, y la
     * primera tarea que el navegador atiende después —un `setTimeout` encolado desde dentro del
     * propio callback— ya tiene la maquetación y el pintado hechos, porque el bucle de eventos los
     * ejecuta antes de volver a las tareas.
     *
     * Lo que esto no incluye, y conviene saberlo al leer el número: la composición en la GPU, que no
     * va en el hilo principal y ninguna API de reloj puede cronometrar desde la página.
     */
    medirCuadro: function(motivo, red, vue, tDom) {
      const self = this;
      if (typeof requestAnimationFrame !== 'function') {
        self.publicarMedida(motivo, red, vue, null, null);
        return;
      }
      requestAnimationFrame(function() {
        const tCuadro = self.ahoraMedido();
        const espera = tCuadro - tDom;
        if (typeof setTimeout !== 'function') {
          self.publicarMedida(motivo, red, vue, null, espera);
          return;
        }
        setTimeout(function() {
          self.publicarMedida(motivo, red, vue, self.ahoraMedido() - tCuadro, espera);
        }, 0);
      });
    },
    /**
     * La línea de la medida, con el mismo prefijo `[FACTCOBRO-TIEMPO]` que las del servidor para
     * que quien mide lo recoja todo con un solo filtro.
     *
     * **No lleva ni un dato de nadie**: milisegundos y el tamaño de lo que se ha pintado. Ni
     * importes, ni códigos de factura, ni quién ha entrado — es la misma condición que la
     * instrumentación de la API, y aquí vale doble porque esto sale en la consola de un equipo.
     */
    publicarMedida: function(motivo, red, vue, frame, espera) {
      const serie = this.serie;
      const ejercicios = serie && serie.ejercicios ? serie.ejercicios.length : 0;
      const filasMesAMes = this.mesAMes && this.mesAMes.filas ? this.mesAMes.filas.length : 0;
      // El total es la suma de los cuatro tramos, espera incluida: es lo que la persona espera de
      // verdad desde que se pide hasta que lo ve, y el reparto de al lado dice en qué se va.
      const total = (red || 0) + (vue || 0) + (espera || 0) + (frame || 0);
      console.info('[FACTCOBRO-TIEMPO] pantalla-ejercicios motivo=' + motivo
        + ' red=' + this.formatMs(red) + ' vue=' + this.formatMs(vue)
        + ' espera-cuadro=' + this.formatMs(espera)
        + ' frame=' + this.formatMs(frame) + ' total=' + this.formatMs(total)
        + ' (ejercicios=' + ejercicios + ' celdas-mes=' + (ejercicios * 12)
        + ' filas-barras=' + (this.barras ? this.barras.length : 0)
        + ' filas-mes-a-mes=' + filasMesAMes
        + ' facturas-a-revisar=' + this.facturasARevisar.length + ')');
    },
    /** Un tramo de la medida, en milisegundos con una décima. Un tramo que no hubo es un guion. */
    formatMs: function(valor) {
      if (valor === null || valor === undefined) return '—';
      return Number(valor).toFixed(1) + 'ms';
    },
    /**
     * El ancho de una barra del cuadro mes a mes, en % de la pista.
     *
     * El recorte al 100 % sí pasa, y a propósito: la escala es el año entero del ejercicio
     * anterior y el año en curso puede superarlo. Lo que no puede pasar es que ese recorte quede
     * mudo —una barra recortada mide la escala en vez de su valor, y dos filas distintas se ven
     * iguales—, y de eso se encargan `rebosaActual`/`rebosaAnterior`: la barra se marca y el pie
     * lo dice.
     */
    anchoPista: function(valor, escala) {
      if (!(escala > 0)) return 0;
      return Math.min(100, Math.max(0, (100 * Number(valor)) / escala));
    },
    /**
     * Una fecha del servidor (`2026-10-02`) como se escribe aquí: `02/10/2026`. Sin `new Date()`,
     * que interpreta la cadena en UTC y en Valladolid puede devolver el día de antes.
     */
    formatFecha: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1] + '/' + partes[0];
    },
    /** Euros enteros, que es como se leen estas cifras. Un hueco es un guion, nunca un cero. */
    formatEuros: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Math.round(Number(valor)).toLocaleString('es-ES');
    },
    formatEurosConSigno: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      const signo = Number(valor) > 0 ? '+' : '';
      return signo + Math.round(Number(valor)).toLocaleString('es-ES');
    },
    formatPorcentaje: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      const signo = Number(valor) > 0 ? '+' : '';
      return signo + Number(valor).toFixed(1).replace('.', ',') + ' %';
    },
    claseVar: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '';
      return Number(valor) >= 0 ? 'var-positivo' : 'var-negativo';
    }
  }
};
</script>
